import styled from '@emotion/styled';
import useToggle from 'react-use/lib/useToggle';
import { useCallback } from 'react';
import { FIELD_TYPES } from 'utility/constants';
import { SortableHeadCell as KizenSortableHeadCell } from '__components/Kizen/Table';
import { getColumnNumeric } from '__components/Fields/InlineTableFieldInput';
import {
  getSizing,
  ResizerLayout,
} from '__pages/Common/components/BigTableCells';
import { DraggableCore } from 'react-draggable';
import Icon from '__components/Kizen/Icon';
import { PADDING } from '__pages/ActivitySettings/pages/ScheduledAndLogged/Table';
import { getFieldsDescription } from 'components/Fields/FieldInput/helpers';

const SortableHeadCell = styled(
  ({
    column,
    minWidth,
    children,
    className,
    onResizeColumnWidth,
    onResizeStop,
    getColumnWidth,
    narrowestWidth = 75,
    resizable = true,
    fieldId,
    objectId,
    objectsById,
    activityField,
    ...others
  }) => {
    const width = getColumnWidth(column.id);
    const [resizing, toggleResizing] = useToggle(false);
    const handleResize = useCallback(
      (ev: any, { x }: any) => {
        const nextWidth = Math.max(narrowestWidth, x - PADDING / 2);
        onResizeColumnWidth({
          id: column.id,
          label: column.label,
          width: `${nextWidth}px`,
          visible: true,
        });
      },
      [column.id, column.label, onResizeColumnWidth, narrowestWidth]
    );

    const onResizeColumnStop = useCallback(() => {
      onResizeStop();
    }, [onResizeStop]);

    let field = null;
    let hasSorting = true;
    if (fieldId && objectId) {
      field = objectsById?.[objectId]?.fieldsById?.[fieldId];
      hasSorting =
        field?.fieldType !== FIELD_TYPES.Relationship.type ||
        field.relation.cardinality === 'many_to_one' ||
        field.relation.cardinality === 'one_to_one';
    }
    const { withNameDescription } = getFieldsDescription({
      ...activityField,
      displayName: column?.label || field?.displayName,
    });
    return (
      <KizenSortableHeadCell
        by={fieldId ? fieldId : undefined}
        numeric={field ? getColumnNumeric(field) : undefined}
        hasSorting={hasSorting}
        className={`${className} ${resizing && 'Resizing'}`}
        column={column}
        description={withNameDescription}
        padding={`${PADDING}px`}
        {...getSizing(width, minWidth)}
        {...others}
      >
        {children}

        {resizable && (
          <ResizerLayout className="ResizerLayout" padding={PADDING}>
            <DraggableCore
              onDrag={handleResize}
              onStart={toggleResizing}
              onStop={() => {
                toggleResizing();
                onResizeColumnStop();
              }}
            >
              <Icon className="Resizer" icon="column-resize" size="intrinsic" />
            </DraggableCore>
          </ResizerLayout>
        )}
      </KizenSortableHeadCell>
    );
  }
)`
  &.Resizing {
    cursor: ew-resize;
    svg line {
      stroke: var(--color-background-darker);
    }
  }
  > div {
    // Fixes small alignment issue with cell text
    line-height: 0;
    margin-top: 1px;
    button {
      margin-left: 10px;
    }
  }
  button {
    margin-left: 5px;
  }
`;
export default SortableHeadCell;
